/**
 * @module components/InvoiceButton
 * @description Invoice action button for viewing or paying invoices
 */

import React, { useMemo } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { PackageButton } from 'src/components/PackageButton';
import { Invoices } from 'src/types/invoices';
import { Sanity } from 'src/types/sanity';
import * as Styles from './styles';
import { InvoiceButtonProps } from './types';

/**
 * @component InvoiceButton
 * @summary Renders payment/view button for invoice based on payment status
 * @type {React.FC<InvoiceButtonProps>}
 * @param {InvoiceButtonProps} props - Component props from types.ts
 * @returns {React.ReactElement | null} Invoice action button or null if no link
 */
export const InvoiceButton: React.FC<InvoiceButtonProps> = ({
  invoice,
  onOpenPaymentLink,
  className,
  ...rest
}) => {
  const invoiceLink = invoice.payment_url || invoice.url;

  const buttonTone = useMemo(
    () => invoice.paid || invoice.status === Invoices.Types.InvoiceStatus.PAID
      ? Sanity.Types.SanityTone.POSITIVE
      : Sanity.Types.SanityTone.CAUTION,
    [invoice.paid, invoice.status],
  );

  const buttonText = useMemo(
    () => invoice.paid || invoice.status === Invoices.Types.InvoiceStatus.PAID
      ? 'View Invoice'
      : 'Pay Invoice',
    [invoice.paid, invoice.status],
  );

  if (!invoiceLink) {
    return null;
  }

  return (
    <Styles.Container className={className} {...rest}>
      <PackageButton
        tone={buttonTone}
        onClick={onOpenPaymentLink}
        icon={<FaExternalLinkAlt />}
      >
        {buttonText}
      </PackageButton>
    </Styles.Container>
  );
};

export default InvoiceButton;
